import { StatusStyle, withoutStringUnion, GenericStatusIndicator, GenericStatusInfoIconName, ProcedureStatus, DraftWaypointStatus } from '@caresend/types';
import { isInEnum } from '@caresend/utils';
import { defineComponent, computed } from 'vue';
import CanceledIcon from '../../../assets/icons/canceled.svg.js';
import Check from '../../../assets/icons/check.svg.js';
import CollectedIcon from '../../../assets/icons/collected.svg.js';
import DeliveredIcon from '../../../assets/icons/delivered.svg.js';
import DeliveryIcon from '../../../assets/icons/delivery.svg.js';
import InProgressIcon from '../../../assets/icons/in_progress.svg.js';
import SubmittedIcon from '../../../assets/icons/submitted.svg.js';
import { StatusIndicatorMode, StatusIndicatorSize } from './model.js';
import __vue_component__ from '../../ui/Skeleton.vue.js';

var script = defineComponent({
    components: {
        CanceledIcon,
        CheckIcon: Check,
        CollectedIcon,
        DeliveredIcon,
        DeliveryIcon,
        InProgressIcon,
        Skeleton: __vue_component__,
        SubmittedIcon,
    },
    props: {
        darkMode: Boolean,
        isLoading: Boolean,
        loadingStyle: {
            type: String,
            default: 'skeleton',
        },
        iconName: {
            type: String,
            default: null,
        },
        label: {
            type: String,
            default: null,
        },
        mode: {
            type: String,
            default: StatusIndicatorMode.GENERIC,
        },
        status: {
            type: String,
            required: true,
        },
        statusStyle: {
            type: String,
            default: StatusStyle.SQUARE,
        },
        statusSize: {
            type: String,
            default: StatusIndicatorSize.MD,
        },
    },
    setup(props) {
        const getValidatedGenericStatus = (status) => {
            if (isInEnum(status, GenericStatusIndicator)) {
                return status;
            }
            return GenericStatusIndicator.GREY;
        };
        const getGenericStatusForProcedure = (status) => {
            if (isInEnum(status, ProcedureStatus)) {
                switch (status) {
                    case ProcedureStatus.CREATED:
                        return GenericStatusIndicator.ORANGE;
                    case ProcedureStatus.COMPLETED:
                        return GenericStatusIndicator.GREEN;
                    case ProcedureStatus.FAILED:
                    case ProcedureStatus.CANCELED:
                        return GenericStatusIndicator.RED;
                }
            }
            return GenericStatusIndicator.GREY;
        };
        const getGenericStatusForDraftWaypoint = (status) => {
            if (isInEnum(status, DraftWaypointStatus)) {
                switch (status) {
                    case DraftWaypointStatus.PENDING:
                        return GenericStatusIndicator.ORANGE;
                    case DraftWaypointStatus.ACCEPTED:
                        return GenericStatusIndicator.BLUE;
                    case DraftWaypointStatus.REJECTED:
                        return GenericStatusIndicator.RED;
                    case DraftWaypointStatus.FINALIZED:
                        return GenericStatusIndicator.GREEN;
                    case DraftWaypointStatus.IN_ITINERARY:
                        return GenericStatusIndicator.CELESTE;
                    case DraftWaypointStatus.CANCELED:
                        return GenericStatusIndicator.RED;
                }
            }
            return GenericStatusIndicator.GREY;
        };
        const computedGenericStatus = computed(() => {
            switch (withoutStringUnion(props.mode)) {
                case StatusIndicatorMode.DRAFT_WAYPOINT:
                    return getGenericStatusForDraftWaypoint(props.status);
                case StatusIndicatorMode.EXACT:
                    return GenericStatusIndicator.EXACT;
                case StatusIndicatorMode.GENERIC:
                    return getValidatedGenericStatus(props.status);
                case StatusIndicatorMode.PROCEDURE:
                    return getGenericStatusForProcedure(props.status);
            }
        });
        const colorClassName = computed(() => {
            if (props.iconName && props.iconName !== GenericStatusInfoIconName.CHECK)
                return '';
            switch (computedGenericStatus.value) {
                case GenericStatusIndicator.EXACT:
                    return 'StatusIndicator--exact';
                case GenericStatusIndicator.BLUE:
                    return 'StatusIndicator--blue';
                case GenericStatusIndicator.CELESTE:
                    return 'StatusIndicator--celeste';
                case GenericStatusIndicator.GREEN:
                    return 'StatusIndicator--green';
                case GenericStatusIndicator.GREY:
                    return 'StatusIndicator--grey';
                case GenericStatusIndicator.ORANGE:
                    return 'StatusIndicator--orange';
                case GenericStatusIndicator.PURPLE:
                    return 'StatusIndicator--purple';
                case GenericStatusIndicator.RED:
                    return 'StatusIndicator--red';
                case GenericStatusIndicator.WHITE:
                    return 'StatusIndicator--white';
                case GenericStatusIndicator.YELLOW:
                    return 'StatusIndicator--yellow';
            }
        });
        const classNames = computed(() => {
            const names = [];
            const iconOnly = props.iconName
                ? [
                    GenericStatusInfoIconName.CIRCLE,
                    GenericStatusInfoIconName.DELIVERY,
                ].includes(props.iconName)
                : false;
            if (!props.isLoading && !iconOnly)
                names.push(colorClassName.value);
            if (props.iconName === GenericStatusInfoIconName.CIRCLE) {
                names.push('StatusIndicator--circle-icon');
            }
            switch (props.statusStyle) {
                case StatusStyle.SQUARE:
                    names.push('StatusIndicator--square');
                    break;
                case StatusStyle.CIRCLE:
                    names.push('StatusIndicator--circle');
            }
            switch (props.statusSize) {
                case StatusIndicatorSize.XS:
                    names.push('StatusIndicator--xs');
                    break;
                case StatusIndicatorSize.SM:
                    names.push('StatusIndicator--sm');
                    break;
                case StatusIndicatorSize.MD:
                    names.push('StatusIndicator--md');
                    break;
                case StatusIndicatorSize.LG:
                    names.push('StatusIndicator--lg');
            }
            if (props.label) {
                names.push('StatusIndicator--with-text');
            }
            if (props.darkMode) {
                names.push('StatusIndicator--dark-mode');
            }
            return names;
        });
        const displayCheckIcon = computed(() => props.iconName === GenericStatusInfoIconName.CHECK);
        const displayDeliveryIcon = computed(() => props.iconName === GenericStatusInfoIconName.DELIVERY);
        const displayCanceledOrFailedIcon = computed(() => props.iconName === GenericStatusInfoIconName.CANCELED || props.iconName === GenericStatusInfoIconName.FAILED);
        const displayInProgressIcon = computed(() => props.iconName === GenericStatusInfoIconName.IN_PROGRESS);
        const displayCollectedIcon = computed(() => props.iconName === GenericStatusInfoIconName.COLLECTED);
        const displaySubmittedIcon = computed(() => props.iconName === GenericStatusInfoIconName.SUBMITTED);
        const displayDeliveredIcon = computed(() => props.iconName === GenericStatusInfoIconName.DELIVERED);
        return {
            classNames,
            displayCanceledOrFailedIcon,
            displayCheckIcon,
            displayCollectedIcon,
            displayDeliveredIcon,
            displayDeliveryIcon,
            displayInProgressIcon,
            displaySubmittedIcon,
        };
    },
});

export { script as default };
